/**
 * Main Layout Component
 * Provides consistent layout with theme toggle across all pages
 */

import { Link, useLocation } from 'react-router-dom';
import { Network } from 'lucide-react';
import { ThemeToggle } from './ThemeToggle';
import { cn } from "@/lib/utils";
import { cambrianColors, typography } from "@/styles/design-tokens";

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const location = useLocation();

  const navItems = [
    { path: '/knowledge-graph', label: 'Knowledge Graph' },
    { path: '/graph-qa', label: 'Graph Q&A' },
    { path: '/erc8004', label: 'ERC-8004 Orchestrator' },
  ];

  return (
    <div className={cn("min-h-screen", cambrianColors.bg.primary)}>
      {/* Header */}
      <header className={cn(
        "border-b",
        cambrianColors.bg.surface,
        cambrianColors.border.primary
      )}>
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            {/* Logo/Title */}
            <Link to="/" className="flex items-center gap-3">
              <Network className={cn("h-8 w-8", cambrianColors.brand.primary)} />
              <h1 className={cn(typography.h2, cambrianColors.text.heading)}>
                Deep42 Research
              </h1>
            </Link>

            {/* Theme Toggle */}
            <ThemeToggle />
          </div>

          {/* Navigation */}
          <nav className="mt-4 flex gap-2 flex-wrap">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={cn(
                  "px-4 py-2 rounded-md transition-colors",
                  typography.body,
                  location.pathname === item.path
                    ? cn(cambrianColors.brand.bg, "text-white")
                    : cn(
                        cambrianColors.text.body,
                        cambrianColors.bg.hover.light,
                        "hover:bg-opacity-100"
                      )
                )}
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main>{children}</main>
    </div>
  );
}
